import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Grid, Card, CardActionArea, CardContent, useMediaQuery, useTheme,
    CircularProgress, Tabs, Tab, IconButton, Chip, Stack, Divider, Button
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import api from '../services/api';
import ContasPagar from './ContasPagar';
import ContasReceber from './ContasReceber';

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const brl = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function TabPanel({ children, value, index, ...other }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`financeiro-tabpanel-${index}`}
            aria-labelledby={`financeiro-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
        </div>
    );
}

/**
 * Indicador do painel. Clicar abre a lista já filtrada — o número e a lista usam
 * o mesmo critério, então o usuário consegue conferir de onde veio o valor.
 */
function Indicador({ titulo, valor, detalhe, cor, icone, onClick, ariaLabel }) {
    const conteudo = (
        <CardContent sx={{ textAlign: 'center', py: 2.5 }}>
            {icone}
            <Typography variant="subtitle2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                {titulo}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: cor }}>
                {valor}
            </Typography>
            {detalhe && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {detalhe}
                </Typography>
            )}
        </CardContent>
    );

    return (
        <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
            {onClick
                ? <CardActionArea onClick={onClick} aria-label={ariaLabel || titulo} sx={{ height: '100%' }}>{conteudo}</CardActionArea>
                : conteudo}
        </Card>
    );
}

export default function Financeiro() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const hoje = new Date();

    const [tabValue, setTabValue] = useState(0);
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState('');
    const [periodo, setPeriodo] = useState({ mes: hoje.getMonth() + 1, ano: hoje.getFullYear() });
    const [resumoReceber, setResumoReceber] = useState(null);
    const [resumoPagar, setResumoPagar] = useState(null);
    const [saldoBancario, setSaldoBancario] = useState(null);
    // Filtro aplicado na lista quando o usuário clica em um indicador
    const [filtroReceber, setFiltroReceber] = useState(null);
    const [filtroPagar, setFiltroPagar] = useState(null);

    const mesParam = `${periodo.ano}-${String(periodo.mes).padStart(2, '0')}`;
    const primeiroDia = `${mesParam}-01`;
    const ultimoDia = new Date(periodo.ano, periodo.mes, 0).toISOString().slice(0, 10);

    const carregar = useCallback(async () => {
        setLoading(true);
        setErro('');
        try {
            // Previsto/obrigação: por vencimento do mês. Realizado: por data de
            // pagamento (movimentações). Saldo bancário: soma das contas — três
            // conceitos diferentes, antes somados no mesmo número chamado "Saldo".
            const [receber, pagar, contas] = await Promise.all([
                api.get('/contas-receber/resumo', {
                    params: { mes: mesParam, dataPagamentoInicio: primeiroDia, dataPagamentoFim: ultimoDia }
                }),
                api.get('/contas-pagar/resumo', {
                    params: { mes: mesParam, dataPagamentoInicio: primeiroDia, dataPagamentoFim: ultimoDia }
                }),
                api.get('/contas-bancarias'),
            ]);
            setResumoReceber(receber.data);
            setResumoPagar(pagar.data);
            const lista = Array.isArray(contas.data) ? contas.data : (contas.data?.content ?? []);
            setSaldoBancario(lista.reduce((soma, c) => soma + Number(c.saldoAtual || 0), 0));
        } catch (e) {
            console.error('Erro ao carregar resumo financeiro:', e);
            setErro('Não foi possível carregar o resumo financeiro.');
        } finally {
            setLoading(false);
        }
    }, [mesParam, primeiroDia, ultimoDia]);

    useEffect(() => { carregar(); }, [carregar]);

    const mudarMes = (delta) => {
        setPeriodo((p) => {
            const d = new Date(p.ano, p.mes - 1 + delta, 1);
            return { mes: d.getMonth() + 1, ano: d.getFullYear() };
        });
    };

    const abrirReceber = (filtro) => {
        setFiltroReceber({ ...filtro, mes: periodo.mes, ano: periodo.ano, _t: Date.now() });
        setTabValue(2);
    };
    const abrirPagar = (filtro) => {
        setFiltroPagar({ ...filtro, mes: periodo.mes, ano: periodo.ano, _t: Date.now() });
        setTabValue(1);
    };

    const recebidoPeriodo = resumoReceber?.recebidoNoPeriodo ?? 0;
    const pagoPeriodo = resumoPagar?.pagoNoPeriodo ?? 0;
    const emAbertoReceber = resumoReceber?.totalEmAberto ?? 0;
    const vencidoReceber = resumoReceber?.totalVencido ?? 0;
    const emAbertoPagar = resumoPagar?.totalEmAberto ?? 0;
    const vencidoPagar = resumoPagar?.totalVencido ?? 0;

    if (loading && !resumoReceber) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress sx={{ color: '#1ABB9C' }} />
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Stack
                direction={isMobile ? 'column' : 'row'}
                spacing={1.5}
                sx={{ mb: 2, alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between' }}
            >
                <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" sx={{ fontWeight: 'bold', color: '#333' }}>
                    Financeiro
                </Typography>

                {/* Período sempre visível: os indicadores abaixo se referem a ele */}
                <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                    <IconButton onClick={() => mudarMes(-1)} aria-label="Mês anterior" size="small">
                        <ChevronLeftIcon />
                    </IconButton>
                    <Chip
                        label={`${MESES[periodo.mes - 1]} de ${periodo.ano}`}
                        sx={{ fontWeight: 600 }}
                    />
                    <IconButton onClick={() => mudarMes(1)} aria-label="Mês seguinte" size="small">
                        <ChevronRightIcon />
                    </IconButton>
                    <IconButton onClick={carregar} aria-label="Atualizar indicadores" size="small">
                        <RefreshIcon />
                    </IconButton>
                </Stack>
            </Stack>

            {erro && (
                <Paper sx={{ p: 2, mb: 2, bgcolor: 'error.light', color: 'error.contrastText' }} role="alert">
                    <Stack direction="row" spacing={2} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="body2">{erro}</Typography>
                        <Button size="small" color="inherit" onClick={carregar}>Tentar de novo</Button>
                    </Stack>
                </Paper>
            )}

            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Indicador
                        titulo="Recebido no período"
                        valor={brl(recebidoPeriodo)}
                        detalhe="Baixas menos estornos, por data de pagamento"
                        cor="#2e7d32"
                        icone={<TrendingUpIcon sx={{ fontSize: 36, color: '#2e7d32' }} />}
                        onClick={() => abrirReceber({ status: 'PAGO' })}
                        ariaLabel="Ver recebimentos do período"
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Indicador
                        titulo="A receber em aberto"
                        valor={brl(emAbertoReceber)}
                        detalhe={`Vencido: ${brl(vencidoReceber)}`}
                        cor="#0277bd"
                        icone={<HourglassEmptyIcon sx={{ fontSize: 36, color: '#0277bd' }} />}
                        onClick={() => abrirReceber({ emAberto: true })}
                        ariaLabel="Ver contas a receber em aberto"
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Indicador
                        titulo="Despesas pagas"
                        valor={brl(pagoPeriodo)}
                        detalhe={`Em aberto: ${brl(emAbertoPagar)}`}
                        cor="#c62828"
                        icone={<TrendingDownIcon sx={{ fontSize: 36, color: '#c62828' }} />}
                        onClick={() => abrirPagar({ status: 'PAGO' })}
                        ariaLabel="Ver despesas pagas no período"
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Indicador
                        titulo="Saldo bancário"
                        valor={saldoBancario == null ? '—' : brl(saldoBancario)}
                        detalhe="Soma das contas cadastradas"
                        cor={(saldoBancario ?? 0) >= 0 ? '#2e7d32' : '#c62828'}
                        icone={<AccountBalanceIcon sx={{ fontSize: 36, color: (saldoBancario ?? 0) >= 0 ? '#2e7d32' : '#c62828' }} />}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Indicador
                        titulo="Recebimentos vencidos"
                        valor={brl(vencidoReceber)}
                        detalhe={`${resumoReceber?.quantidadeVencido ?? 0} título(s)`}
                        cor="#ef6c00"
                        icone={<EventBusyIcon sx={{ fontSize: 36, color: '#ef6c00' }} />}
                        onClick={() => abrirReceber({ vencidas: true })}
                        ariaLabel="Ver contas a receber vencidas"
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Indicador
                        titulo="Despesas vencidas"
                        valor={brl(vencidoPagar)}
                        detalhe={`${resumoPagar?.quantidadeVencido ?? 0} título(s)`}
                        cor="#ef6c00"
                        icone={<EventBusyIcon sx={{ fontSize: 36, color: '#ef6c00' }} />}
                        onClick={() => abrirPagar({ vencidas: true })}
                        ariaLabel="Ver contas a pagar vencidas"
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Indicador
                        titulo="Resultado do período"
                        valor={brl(Number(recebidoPeriodo) - Number(pagoPeriodo))}
                        detalhe="Recebido menos despesas pagas (caixa)"
                        cor={Number(recebidoPeriodo) - Number(pagoPeriodo) >= 0 ? '#2e7d32' : '#c62828'}
                        icone={<TrendingUpIcon sx={{ fontSize: 36, color: '#607d8b' }} />}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Indicador
                        titulo="Cancelados no período"
                        valor={brl(resumoReceber?.totalCancelado ?? 0)}
                        detalhe="Fora da obrigação ativa"
                        cor="#607d8b"
                        icone={<EventBusyIcon sx={{ fontSize: 36, color: '#607d8b' }} />}
                        onClick={() => abrirReceber({ status: 'CANCELADO' })}
                        ariaLabel="Ver títulos cancelados"
                    />
                </Grid>
            </Grid>

            <Box sx={{ mt: 4 }}>
                <Paper elevation={0} sx={{ bgcolor: 'transparent', borderRadius: 2 }}>
                    <Tabs
                        value={tabValue}
                        onChange={(e, v) => setTabValue(v)}
                        aria-label="Seções do financeiro"
                        sx={{
                            borderBottom: 1,
                            borderColor: 'divider',
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: isMobile ? '0.875rem' : '1rem',
                            }
                        }}
                    >
                        <Tab label="Visão do mês" id="financeiro-tab-0" aria-controls="financeiro-tabpanel-0" />
                        <Tab label="Contas a Pagar" id="financeiro-tab-1" aria-controls="financeiro-tabpanel-1" />
                        <Tab label="Contas a Receber" id="financeiro-tab-2" aria-controls="financeiro-tabpanel-2" />
                    </Tabs>

                    <TabPanel value={tabValue} index={0}>
                        <Paper elevation={0} sx={{ p: 3, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                            <Typography variant="h6" sx={{ mb: 2, color: '#333' }}>
                                {MESES[periodo.mes - 1]} de {periodo.ano}
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>Recebimentos</Typography>
                                    <Divider sx={{ my: 1 }} />
                                    <Linha rotulo="Previsto (vence no mês)" valor={brl(resumoReceber?.totalGeral ?? 0)} />
                                    <Linha rotulo="Liquidado dos títulos do mês" valor={brl(resumoReceber?.totalRecebido ?? 0)} />
                                    <Linha rotulo="Recebido no mês (caixa)" valor={brl(recebidoPeriodo)} />
                                    <Linha rotulo="Em aberto a vencer" valor={brl(resumoReceber?.totalPendente ?? 0)} />
                                    <Linha rotulo="Em aberto vencido" valor={brl(vencidoReceber)} destaque />
                                    <Linha rotulo="Títulos parciais" valor={`${resumoReceber?.quantidadeParcial ?? 0}`} />
                                </Grid>

                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>Despesas</Typography>
                                    <Divider sx={{ my: 1 }} />
                                    <Linha rotulo="Previsto (vence no mês)" valor={brl(resumoPagar?.totalGeral ?? 0)} />
                                    <Linha rotulo="Liquidado dos títulos do mês" valor={brl(resumoPagar?.totalPago ?? 0)} />
                                    <Linha rotulo="Pago no mês (caixa)" valor={brl(pagoPeriodo)} />
                                    <Linha rotulo="Em aberto a vencer" valor={brl(resumoPagar?.totalPendente ?? 0)} />
                                    <Linha rotulo="Em aberto vencido" valor={brl(vencidoPagar)} destaque />
                                    <Linha rotulo="Títulos parciais" valor={`${resumoPagar?.quantidadeParcial ?? 0}`} />
                                </Grid>
                            </Grid>

                            <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.secondary' }}>
                                Previsto usa a data de vencimento; o caixa usa a data de pagamento das
                                movimentações (já descontados estornos). Títulos cancelados não entram
                                na obrigação do mês. O saldo bancário vem das contas cadastradas.
                            </Typography>
                        </Paper>
                    </TabPanel>

                    <TabPanel value={tabValue} index={1}>
                        <ContasPagar filtroInicial={filtroPagar} />
                    </TabPanel>

                    <TabPanel value={tabValue} index={2}>
                        <ContasReceber filtroInicial={filtroReceber} />
                    </TabPanel>
                </Paper>
            </Box>
        </Box>
    );
}

function Linha({ rotulo, valor, destaque }) {
    return (
        <Stack direction="row" sx={{ justifyContent: 'space-between', py: 0.4 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>{rotulo}</Typography>
            <Typography variant="body2" sx={{ fontWeight: destaque ? 700 : 600, color: destaque ? '#ef6c00' : 'text.primary' }}>
                {valor}
            </Typography>
        </Stack>
    );
}
